/**
 * DataSource: Contatos (`contacts`) — GUPM-158 (emergência) + GUPM-200 (suporte técnico)
 * ------------------------------------------------------------------------------------
 * Ponto único de leitura de `contacts`. 🟠 sensível (nome/telefone): RLS `contacts_read`
 * (GUPM-128) só libera leitura a usuário AUTENTICADO. Nada hardcoded — vem do banco.
 *
 * GUPM-202 — contato único por pessoa:
 *  - `is_emergencia` (marcação) separa a lista de emergência — exibida no modal do
 *    Header, na Cobertura Técnica e no PAEBM (`listAtivos`). A `categoria` antiga
 *    continua no banco só para o front 1.12.0 e não é mais lida aqui.
 *  - o diretório por empresa (`listSuporteTecnico`) é de quem tem empresa; um
 *    contato de emergência com empresa aparece nos dois.
 *  - leituras pela view `contatos_resolvidos`: nome e e-mail do usuário do app
 *    vinculado (`app_user_id`), com o gravado no contato como reserva.
 * `listAtivos` filtra a marcação para NÃO vazar o diretório no fluxo crítico de
 * emergência. A PII real (nome/telefone/e-mail) entra só pela tela do admin, nunca
 * versionada (GUPM-158/200).
 *
 * C-75: o admin cria, edita, desativa e reativa contatos pela tela. O gate real é a
 * RLS: INSERT/UPDATE só para `admin`, sem DELETE (migration 20261013120000). A
 * gravação vai à tabela `contacts`; a view é só leitura.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type { DataSourceMeta } from './types';
import { CONTATO_SUPORTE_APP_ID } from '@/domain/suporte';
import type { ContatoNormalizado } from '@/domain/contatos/validacao';
import { MINIMO_EMERGENCIA_ATIVOS } from '@/domain/contatos/regras';

/** Contato de emergência exposto às telas (subconjunto seguro de `contacts`). */
export interface ContatoEmergencia {
  id: string;
  /** Funções (etiquetas), ex.: ["RT Foscentro", "Coordenador PAEBM"]. GUPM-202. */
  funcoes: string[];
  /** Funções unidas por " / " (ordena a lista e agrupa interna/externa). */
  role: string;
  name: string;
  /** Telefone bruto (para `tel:`); `''` quando pendente (sem link de ligação). */
  phone: string;
  /** Telefone formatado para exibição (ou "[A definir]" quando pendente). */
  display: string;
  /** Disponibilidade (ex.: "Plantão 24h"); `null` quando não se aplica. */
  disponibilidade: string | null;
  /** E-mail (🟠 PII), mostrado na aba Contatos (C-75); o modal do Header não usa. */
  email?: string | null;
  /** Empresa (todo contato novo tem; GUPM-202). */
  empresa?: string | null;
}

/** Contato do diretório por empresa (subconjunto seguro de `contacts`). */
export interface ContatoSuporte {
  id: string;
  /** Empresa — agrupa na UI (casa com `SUPORTE_TECNICO[].empresa`). */
  empresa: string;
  /** Funções (etiquetas). GUPM-202. */
  funcoes: string[];
  /** Funções unidas por " / ". */
  role: string;
  name: string;
  /** Telefone bruto (para `tel:`); `''` quando pendente. */
  phone: string;
  /** Texto de exibição (telefone formatado ou placeholder). */
  display: string;
  /** E-mail do contato (🟠 PII), com link `mailto:` na linha (GUPM-203). */
  email: string | null;
  disponibilidade: string | null;
  /** Também está na lista de emergência (selo 🚨 discreto no diretório). */
  is_emergencia: boolean;
}

/** Linha completa para a tela de gestão (admin) — ativos E inativos. C-75 + GUPM-202. */
export interface ContatoGestao {
  id: string;
  is_emergencia: boolean;
  funcoes: string[];
  role: string;
  name: string;
  phone: string;
  display: string;
  email: string | null;
  empresa: string | null;
  disponibilidade: string | null;
  is_active: boolean;
  sort_order: number;
  /** Usuário do app vinculado (nome e e-mail vêm dele), ou `null`. */
  app_user_id: string | null;
  /** O usuário vinculado está ativo (senão, nome/e-mail são os últimos conhecidos). */
  vinculo_ativo: boolean;
}

/** Usuário ativo do app, para o vínculo no formulário (de `app_users_public`). */
export interface UsuarioDoApp {
  id: string;
  name: string;
  email: string | null;
}

/** Dados de um contato novo. */
export type NovoContato = ContatoNormalizado & { sort_order: number };

/** Dados editáveis de um contato existente. */
export type EdicaoContato = ContatoNormalizado;

/**
 * Falha de gravação com o motivo que a tela mostra. `permissao` cobre as duas
 * formas de recusa do banco: 42501 (GRANT/WITH CHECK) e zero linhas afetadas
 * (USING da policy) — esta última chegaria como sucesso calado sem o `.single()`.
 * `codigo` guarda o código do PostgREST/Postgres para diagnóstico (não tem PII).
 */
export class ErroContato extends Error {
  constructor(
    readonly motivo: 'permissao' | 'sessao' | 'validacao' | 'rede' | 'desconhecido',
    message: string,
    readonly codigo?: string,
  ) {
    super(message);
    this.name = 'ErroContato';
  }
}

// ── Contrato ────────────────────────────────────────────────
export interface ContatosDataSource extends DataSourceMeta {
  /** Contatos de emergência ativos (marcação), ordenados por `role`. */
  listAtivos(): Promise<ContatoEmergencia[]>;
  /** Diretório por empresa: contatos ativos com empresa, ordenados por `sort_order`. */
  listSuporteTecnico(): Promise<ContatoSuporte[]>;
  /** E-mail bruto do suporte do app (linha `CONTATO_SUPORTE_APP_ID`); `null` sem linha. GUPM-336. */
  getEmailSuporteApp(): Promise<string | null>;
  /** C-75 — todos os contatos, ativos e inativos, para a gestão do admin. */
  listParaGestao(): Promise<ContatoGestao[]>;
  /** GUPM-202 — usuários ativos do app, para vincular a um contato. */
  listUsuariosDoApp(): Promise<UsuarioDoApp[]>;
  /** C-75 — só admin (RLS). Lança `ErroContato`. */
  criar(dados: NovoContato): Promise<ContatoGestao>;
  /** C-75 — só admin (RLS). Lança `ErroContato`. */
  atualizar(id: string, dados: EdicaoContato): Promise<ContatoGestao>;
  /** C-75 — desativar/reativar (nunca apaga). Só admin (RLS). Lança `ErroContato`. */
  definirAtivo(id: string, ativo: boolean): Promise<ContatoGestao>;
}

const VIEW = 'contatos_resolvidos';

const COLUNAS_GESTAO =
  'id, is_emergencia, funcoes, role, name, phone, display, email, empresa, disponibilidade, is_active, sort_order, app_user_id, vinculo_ativo';

/** As mesmas, lidas da tabela no retorno da gravação (a tabela não tem `vinculo_ativo`). */
const COLUNAS_GRAVACAO =
  'id, is_emergencia, funcoes, role, name, phone, display, email, empresa, disponibilidade, is_active, sort_order, app_user_id';

/** Índice único do vínculo (migration 20261015140000): um usuário, um contato. */
const INDICE_VINCULO = 'contacts_app_user_id_uniq';

const MSG_REDE = 'Não foi possível salvar o contato. Verifique a conexão e tente novamente.';

/** Traduz o erro do PostgREST no motivo que a tela mostra. Sem PII na mensagem. */
function erroDeGravacao(error: { code?: string; message: string }): ErroContato {
  const code = error.code;
  // Sem código = sem resposta do servidor: o supabase-js não lança quando o fetch
  // é rejeitado, devolve `error` com `code: ''`.
  if (!code) {
    console.error('[contatos] gravação sem resposta do servidor');
    return new ErroContato('rede', MSG_REDE);
  }
  // Só o código vai ao console: a mensagem do Postgres pode citar o valor recusado.
  console.error('[contatos] gravação recusada:', code);
  switch (code) {
    case '42501':
      return new ErroContato('permissao', 'Sem permissão para editar contatos', code);
    case 'PGRST116':
      return new ErroContato('permissao', 'Contato não encontrado ou sem permissão para editá-lo', code);
    case 'PGRST301':
    case 'PGRST303':
      return new ErroContato('sessao', 'Sua sessão expirou. Entre de novo e repita a alteração.', code);
    case '23514':
    case '23502':
    case '22001':
      return new ErroContato('validacao', 'O banco recusou um valor fora do limite. Revise os campos.', code);
    case '23505':
      // A mensagem do Postgres cita o nome do índice (não o valor).
      if (error.message.includes(INDICE_VINCULO)) {
        return new ErroContato('validacao', 'Este usuário já está vinculado a outro contato', code);
      }
      return new ErroContato('validacao', 'Já existe um contato com este identificador.', code);
    // Trigger `contacts_guarda_desativacao` (migrations 20261014120000 e 20261015140000).
    case 'C7501':
      return new ErroContato('validacao', `É preciso manter ao menos ${MINIMO_EMERGENCIA_ATIVOS} contatos de emergência ativos.`, code);
    case 'C7502':
      return new ErroContato('validacao', 'O contato de suporte do app não pode ser desativado.', code);
    default:
      return new ErroContato('desconhecido', `O banco recusou a gravação (código ${code}).`, code);
  }
}

type LinhaGravada = Omit<ContatoGestao, 'vinculo_ativo'>;

// ── Implementação (leitura direta do Supabase, gate por RLS) ─
/**
 * Recebe o cliente para que os testes de RLS rodem o MESMO código com um usuário
 * real de cada perfil. O app usa o cliente único de `@/lib/supabase`.
 */
export function criarContatosDataSource(client: SupabaseClient): ContatosDataSource {
  async function gravar(
    consulta: PromiseLike<{ data: LinhaGravada | null; error: { code?: string; message: string } | null }>,
  ): Promise<ContatoGestao> {
    let resultado;
    try {
      resultado = await consulta;
    } catch {
      // fetch rejeitado: sem resposta do servidor.
      throw new ErroContato('rede', MSG_REDE);
    }
    if (resultado.error) throw erroDeGravacao(resultado.error);
    if (!resultado.data) throw new ErroContato('permissao', 'Sem permissão para editar contatos');
    // O retorno vem da tabela: nome e e-mail são os gravados (no vínculo, a cópia
    // do usuário). A tela relê a lista pela view logo depois (invalidação).
    return { ...resultado.data, vinculo_ativo: resultado.data.app_user_id !== null };
  }

  return {
    mode: 'manual',

    async listAtivos() {
      const { data, error } = await client
        .from(VIEW)
        .select('id, funcoes, role, name, phone, display, disponibilidade, email, empresa')
        .eq('is_emergencia', true) // não vazar o diretório no modal PAEBM
        .eq('is_active', true)
        .order('role')
        .returns<ContatoEmergencia[]>();
      if (error) throw error;
      return data ?? [];
    },

    async listSuporteTecnico() {
      const { data, error } = await client
        .from(VIEW)
        .select('id, empresa, funcoes, role, name, phone, display, email, disponibilidade, is_emergencia')
        .not('empresa', 'is', null)
        .eq('is_active', true)
        .order('sort_order')
        .returns<ContatoSuporte[]>();
      if (error) throw error;
      return data ?? [];
    },

    async getEmailSuporteApp() {
      const { data, error } = await client
        .from(VIEW)
        .select('email')
        .eq('id', CONTATO_SUPORTE_APP_ID)
        .eq('is_active', true)
        .maybeSingle<{ email: string | null }>();
      if (error) throw error;
      return data?.email ?? null;
    },

    async listParaGestao() {
      const { data, error } = await client
        .from(VIEW)
        .select(COLUNAS_GESTAO)
        .order('sort_order')
        .order('role')
        .returns<ContatoGestao[]>();
      if (error) throw error;
      return data ?? [];
    },

    async listUsuariosDoApp() {
      const { data, error } = await client
        .from('app_users_public')
        .select('id, name, email')
        .order('name')
        .returns<UsuarioDoApp[]>();
      if (error) throw error;
      return data ?? [];
    },

    criar(dados) {
      return gravar(client.from('contacts').insert(dados).select(COLUNAS_GRAVACAO).single<LinhaGravada>());
    },

    atualizar(id, dados) {
      return gravar(
        client.from('contacts').update(dados).eq('id', id).select(COLUNAS_GRAVACAO).single<LinhaGravada>(),
      );
    },

    definirAtivo(id, ativo) {
      return gravar(
        client
          .from('contacts')
          .update({ is_active: ativo })
          .eq('id', id)
          .select(COLUNAS_GRAVACAO)
          .single<LinhaGravada>(),
      );
    },
  };
}

export const supabaseContatos: ContatosDataSource = criarContatosDataSource(supabase);
